import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, X, Sparkles, Flag } from 'lucide-react';
import KitSheetList from './KitSheetList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tool-crib`;

// value: [{id, category, subgroup, name, label, qty}], onChange(next)
// The same scroll-and-tick tool sheet the crib uses — every category open, paper order, tap to tick.
const DispatchToolPicker = ({ value = [], onChange, testid = 'dr-tool-picker' }) => {
  const [search, setSearch] = useState('');
  const [auto, setAuto] = useState([]);
  const [hints, setHints] = useState([]);

  const cart = useMemo(() => {
    const m = {};
    value.forEach((t) => { m[t.id] = { item: t, qty: t.qty || 1 }; });
    return m;
  }, [value]);

  // what the crib's companion rules will drop in the box for this list (shield → head gear, welder → leads…)
  useEffect(() => {
    if (!value.length) { setAuto([]); setHints([]); return undefined; }
    let on = true;
    const t = setTimeout(() => {
      axios.post(`${API}/rules/preview`, { items: value.map((v) => ({ catalog_id: v.id, label: v.label, qty: v.qty || 1 })) })
        .then((r) => { if (on) { setAuto(r.data.auto || []); setHints(r.data.hints || []); } }).catch(() => {});
    }, 400);
    return () => { on = false; clearTimeout(t); };
  }, [value]);

  const bump = (it, d) => {
    const cur = cart[it.catalog_id]?.qty || 0;
    const qty = Math.max(0, cur + d);
    if (qty === 0) { onChange(value.filter((t) => t.id !== it.catalog_id)); return; }
    if (cur === 0) {
      onChange([...value, { id: it.catalog_id, category: it.category, subgroup: it.subgroup || null, name: it.name, label: it.label, qty }]);
    } else {
      onChange(value.map((t) => (t.id === it.catalog_id ? { ...t, qty } : t)));
    }
  };

  const autoMap = useMemo(() => Object.fromEntries(auto.map((a) => [a.catalog_id, a])), [auto]);
  const hintMap = useMemo(() => Object.fromEntries(hints.map((h) => [h.catalog_id, h])), [hints]);
  const hintMsgs = useMemo(() => [...new Set(hints.map((h) => h.message))], [hints]);

  return (
    <div data-testid={testid}>
      {value.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="dr-tool-selected">
          {value.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 bg-[#1e3a5f] text-white text-xs font-medium pl-2 pr-1 py-1 rounded-md">
              {t.qty > 1 && <span className="opacity-80">{t.qty}×</span>}{t.label}
              <button type="button" onClick={() => onChange(value.filter((x) => x.id !== t.id))} className="hover:bg-slate-900/10 rounded p-0.5" data-testid={`dr-tool-remove-${t.id}`}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      {auto.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5 text-xs text-violet-900 bg-violet-50 border border-violet-200 rounded-md px-2 py-1.5" data-testid="dr-tool-auto">
          <Sparkles size={13} className="text-violet-600 shrink-0" />
          <span className="font-semibold">Packed automatically with these:</span>
          {auto.map((a) => <span key={a.catalog_id} className="bg-white border border-violet-200 rounded px-1.5 py-0.5" title={`${a.rule} — for ${(a.for || []).join(', ')}`} data-testid={`dr-tool-auto-${a.catalog_id}`}>{a.qty}× {a.name}</span>)}
        </div>
      )}
      {hintMsgs.length > 0 && (
        <div className="mb-3 flex flex-col gap-1 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="dr-tool-hints">
          {hintMsgs.map((m) => <span key={m} className="inline-flex items-center gap-1.5"><Flag size={13} className="text-amber-600 shrink-0" />{m} <span className="text-amber-700">(flagged on the list below)</span></span>)}
        </div>
      )}
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={15} />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter the tool list… or just scroll and tick"
          className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent outline-none" data-testid="dr-tool-search" />
      </div>
      <div className="border border-gray-200 rounded-lg overflow-hidden flex h-[60vh] min-h-[360px]">
        <KitSheetList cart={cart} bump={bump} auto={autoMap} hints={hintMap} query={search} hideStock />
      </div>
    </div>
  );
};

export default DispatchToolPicker;
